import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createVNode as _createVNode, createElementVNode as _createElementVNode, withCtx as _withCtx, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, toDisplayString as _toDisplayString, createElementBlock as _createElementBlock, createTextVNode as _createTextVNode } from "vue"

const _hoisted_1 = /*#__PURE__*/_createElementVNode("span", null, "Save Record", -1)
const _hoisted_2 = {
  key: 1,
  class: "flex md:items-center md:space-x-3 md:space-y-0 space-y-3 md:w-auto w-full md:flex-row flex-col md:mt-0 mt-6"
}
const _hoisted_3 = /*#__PURE__*/_createElementVNode("span", null, "Edit", -1)
const _hoisted_4 = { key: 1 }
const _hoisted_5 = /*#__PURE__*/_createElementVNode("span", null, "View Patient Record", -1)
const _hoisted_6 = {
  key: 0,
  class: "text-[10px] mt-1 text-red-600",
  id: "email-error"
}
const _hoisted_7 = /*#__PURE__*/_createElementVNode("span", null, "View SCAR", -1)
const _hoisted_8 = { class: "mt-6" }
const _hoisted_9 = { class: "lg:sticky static lg:my-10 top-10 lg:h-[70dvh] lg:pb-10 overflow-auto lg:mt-0 mt-6" }
const _hoisted_10 = /*#__PURE__*/_createElementVNode("span", null, "View PDF", -1)

import { computed, ref, watch } from "vue";
import UserLayout from "@/layouts/UserLayout.vue";
import ButtonComponent from "@/components/buttons/ButtonComponent.vue";
import FormSection from "./FormSection.vue";
import SideNav from "./Components/SideNav.vue";
import ConfirmationModal from "@/components/modals/ConfirmationModal.vue";
import SuccessModal from "@/components/modals/SuccessModal.vue";
import { EyeIcon } from "@heroicons/vue/24/solid";
import { useRouter, useRoute } from "vue-router";
import { CreatePatient } from "@/interfaces/PatientInterface";
import CaseService from "@/classes/Cases/CaseService";
import { errorResponse } from "@/helpers/errorMessage";
import { hasPermission } from "@/classes/AuthService";
import PageLoader from "@/components/loaders/PageLoader.vue"; 
import pickBy from "lodash/pickBy"; 
import ContentModal from "./otherInformations/intakes/Component/ContentModal.vue";
import TextInput from "@/components/inputs/TextInput.vue";
import { onIonViewWillEnter } from "@ionic/vue"; 
import SaveIcon from "@/components/icons/saveIcon.vue";
import PencilUnderlineIcon from "@/components/icons/pencilUnderlineIcon.vue"; 
import CaseHistoryService from "@/classes/Cases/CaseHistory/CaseHistoryService";
import { useNetworkStore } from "@/store/network";
import PatientRecordService from "@/classes/Patients/PatientRecordService";
import PatientOfflineForm from "@/classes/Patients/Offline/Formatters/PatientOfflineForm";
import CaseOfflineForm from "@/classes/Cases/CaseHistory/Offline/Formatters/CaseOfflineForm";
import useVuelidate from "@vuelidate/core";
import ErrorValidation from "@/views/interventions/Components/Classess/ErrorValidation";
import Rules from "@/classes/Patients/Offline/Validation/Rules";
import { useRecordStatusChecker } from "@/store/recordStatusChecker";
import OnlineSynching from "@/classes/Synching/OnlineSynching";
import ErrorModal from "@/components/modals/ErrorModal.vue";


export default /*#__PURE__*/_defineComponent({
  __name: 'EditPage',
  setup(__props) {

const router = useRouter();
const route = useRoute(); 
const networkStore = useNetworkStore();
const isNetworkAvailable = computed(() => networkStore.isNetworkAvailable);
const recordStatusChecker = useRecordStatusChecker();
const isRecordDeleted = computed(() => recordStatusChecker.isDeleted);
const onlineSync = new OnlineSynching();
// Set to False to enable create other details
const newRecord = ref<boolean>(false);
const showConfirmation = ref<boolean>(false);
const showSuccess = ref<boolean>(false);
const successMessage = ref<string>("Data has been successfully saved!");
const additionalData = ref<object>({}); 
const patientId = ref<number | null>(route.params.patient);
const caseId = ref<number | null>(route.params.case);
const item = ref<object>({});
const loaded = ref<boolean>(false);
const form = ref<CreatePatient>(PatientOfflineForm.createPatientCaseForm());
const errors = ref<object>({});
const loading = ref<boolean>(false); 
const showContent = ref(false);  
const otherDetailsCount = ref<{
	guardians: number;
	companions: number;
	patientAddresses: number;
	perpetrators: number;
	interventions: number;
	caseConferences: number;
}>({
    guardians: 0,
    companions: 0,
    patientAddresses: 0,
    perpetrators: 0,
    interventions: 0,
    caseConferences: 0,
});

const formContent = ref({
	to: null,
	attn: null
})
const { createPatienCasetRules } = Rules
const showError = ref(false);
const showErrorMessage = ref(null);

/**
 * Validate the form using Vuelidator
 */
 const validateForm = async () =>{
	const vuelidate = useVuelidate(createPatienCasetRules({...form.value}), form.value)
	const result = await vuelidate.value.$validate();

    if (result) {
        return null;
    } else {
        errors.value = ErrorValidation.getErrors(vuelidate.value);
        errorResponse(errors.value);
        return errors.value;
    }
}

const save = async (): Promise<any> => {
	loading.value = true;
	errors.value = {};

    // Set validation rule for Online and Offline
    const validate = await validateForm();
    if (validate) {
        loading.value = false;
        showConfirmation.value = false;
        return;
    }

    const response = await CaseHistoryService.update(
		patientId.value,
		caseId.value,
		{ ...form.value },
        isNetworkAvailable.value
	);

    if (response?.status === 200 || response?.data != undefined) {
        await getData();
        loading.value = false;
        showSuccess.value = true;
        showConfirmation.value = false;
        successMessage.value = response.data.message;
    } else {
        const error = response;
        errors.value = errorResponse(error.response?.data?.errors);
        showConfirmation.value = false;
        loading.value = false;
    }

};

const redirectSuccess = (): void => {
	showSuccess.value = false;
	newRecord.value = false;
    router.push(`/patient/${patientId.value}/cases/${caseId.value}/edit`);
};

const template = (): void => {
	form.value.mental_health = additionalData.value.mentalHealthDiagnoses?.map((item) => {
		const newForm = form.value.mental_health?.filter((mental_health) => {
			if (mental_health.mental_health_diagnoses_id == item.id) {
				return {
					mental_health_diagnoses_id: item.id,
					type: mental_health.type,
					specify: mental_health.specify,
					behavior_type_id: mental_health.behavior_type_id,
					checked: true,
				};
			}
		}).map((mental_health) => {
            return {
                mental_health_diagnoses_id: item.id,
                type: mental_health.type,
                specify: mental_health.specify,
                behavior_type_id: mental_health.behavior_type_id,
                checked: true,
            };
        });
		return newForm?.length > 0
			? newForm[0]
			: { mental_health_diagnoses_id: "", type: "", specify: "", behavior_type_id: "", checked: false };
	});
	form.value.comorbidities = additionalData.value.comorbidities?.map((item) => {
		const newForm = form.value.comorbidities.filter((comorbidity) => {
			if (comorbidity.comorbidity_id == item.id) {
				return {
					comorbidity_id: item.id,
					checked: true,
				};
			}
		}).map((comorbidity) => {
            if (comorbidity.comorbidity_id == item.id) {
				return {
					comorbidity_id: item.id,
					checked: true,
				};
			}
        });
		return newForm?.length > 0 ? newForm[0] : { comorbidity_id: null, checked: false };
	});
};

const fillUpForm = (item: object): void => {
    form.value = {
        ...PatientOfflineForm.fillPatientForm(item.patient),
        ...CaseOfflineForm.fillCaseForm(item),
    }
	form.value.is_reabuse = form.value.is_reabuse ? 1 : 0;
};

const getData = async (): Promise<any> => {
	loading.value = true;
    const response = await CaseHistoryService.edit(
        patientId.value, 
        caseId.value,
        pickBy({}),
        isNetworkAvailable.value,
    );

    if (response?.response?.status === 404) {
        loading.value = false;
        return;
    }
    
    if (response?.status === 200 || response?.data != undefined) {
    
			additionalData.value = response.data;

            item.value = {
                ...response.data.item,
                patient: response.data.patient,
                hasCaseConference: response.data?.hasCaseConference,
            };
            
            if (isNetworkAvailable.value) {
                await PatientRecordService.syncToOffline(response.data?.downloadables);
            }

        fillUpForm(item.value);
        template();

        newRecord.value = route.query?.viewForm ? route.query.viewForm == 'true' : false;
        loaded.value = true;
        loading.value = false;
    } else {
        const error = response;
        loading.value = false;
        console.log(error);
    }
};

const generatePDFAccess = (type:string):void => {
	CaseService
		.generatePDFAccess(
			patientId.value,
			caseId.value,
			{
				...formContent.value,
				type: type
			}
		)
		.then(({data}) => {
			if(data.token){
				if(type == 'scar'){
					downloadScar(data.token)
					showContent.value = false
				} else {
					downloadPatientRecord(data.token)
				}
			}
		}).catch((error) => {
			errors.value = errorResponse(error.response?.data?.errors)
		})

}
const downloadPatientRecord = (token:string):void => {
	CaseService
		.downloadPatientRecord(
			patientId.value,
			caseId.value,
			token 
		)
		.then(({data}) => {
			console.log(data);
		}).catch((error) => {
			console.log(error);
			
		})
}

const downloadScar = (token:string):void => {
	CaseService
		.downloadScar(
			patientId.value,
			caseId.value,
			token, 
			{
				...formContent.value,
				type: 'scar'
			}
		)
		.then(({data}) => {
			console.log(data);
		}).catch((error) => {
			errors.value = errorResponse(error.response?.data?.errors)
		})
}

watch(
    () => isRecordDeleted.value,
    async (val) => {
        if (val) {
            loading.value = false;
            showError.value = false;
            try {
                await getData();
                await onlineSync.checkDrafts();
            } catch (error) {
                loading.value = false;
                showError.value = true;
                showErrorMessage.value = error;
            }
        }
    }
);

watch(
    () => isNetworkAvailable.value,
    async () => {
        loading.value = false;
        showError.value = false;
        try {
            await getData();
            await onlineSync.checkDrafts();
        } catch (error) {
            loading.value = false;
            showError.value = true;
            showErrorMessage.value = error;
        }

    }
);
 
onIonViewWillEnter(async () => {
    showError.value = false;
    try {
        await getData(); 
        await onlineSync.checkDrafts();
    } catch (error) {
        loading.value = false;
        showError.value = true;
        showErrorMessage.value = error;
    }

});

 

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createBlock(UserLayout, {
    hasBack: "",
    hasRightCon: "",
    backLink: `/patient/${_unref(route).params.patient}/view`,
    backTitle: "Patient and Case Details",
    hasBackClass: "flex md:justify-between md:items-center md:flex-row flex-col"
  }, {
    lowerRight: _withCtx(() => [
      (newRecord.value && _unref(hasPermission)('can-update-patient-incident'))
        ? (_openBlock(), _createBlock(ButtonComponent, {
            key: 0,
            customClass: "primary md:w-auto w-full md:mt-0 mt-6",
            form: "form",
            onClick: _cache[0] || (_cache[0] = ($event: any) => (showConfirmation.value = true))
          }, {
            default: _withCtx(() => [
              _createVNode(SaveIcon, { class: "object-contain w-5 h-5 mr-1 icon" }),
              _hoisted_1
            ]),
            _: 1
          }))
        : _createCommentVNode("", true),
      (!newRecord.value)
        ? (_openBlock(), _createElementBlock("div", _hoisted_2, [
            (_unref(hasPermission)('can-update-patient-incident'))
              ? (_openBlock(), _createBlock(ButtonComponent, {
                  key: 0,
                  onClick: _cache[1] || (_cache[1] = ($event: any) => (newRecord.value = true)),
                  customClass: "primary md:w-auto w-full"
                }, {
                  default: _withCtx(() => [
                    _createVNode(PencilUnderlineIcon, { class: "object-contain w-4 h-4 mr-1 icon" }),
                    _hoisted_3
                  ]),
                  _: 1
                }))
              : _createCommentVNode("", true),
            (_unref(hasPermission)('can-view-patient-case-inpdf') && isNetworkAvailable.value)
              ? (_openBlock(), _createElementBlock("a", _hoisted_4, [
                  _createVNode(ButtonComponent, {
                    onClick: _cache[2] || (_cache[2] = ($event: any) => (generatePDFAccess('profile'))),
                    size: "large",
                    customClass: "bg-green-600 hover:bg-green-700 text-white transition border border-green-600 hover:border-green-700 md:w-auto w-full"
                  }, {
                    default: _withCtx(() => [
                      _createVNode(_unref(EyeIcon), { class: "w-5 h-5 mr-2 -ml-1 icon" }),
                      _hoisted_5
                    ]),
                    _: 1
                  }),
                  (errors.value?.to)
                    ? (_openBlock(), _createElementBlock("p", _hoisted_6, _toDisplayString(errors.value?.to), 1))
                    : _createCommentVNode("", true)
                ]))
              : _createCommentVNode("", true),
            (isNetworkAvailable.value)
              ? (_openBlock(), _createBlock(ButtonComponent, {
                  key: 2,
                  size: "large",
                  onClick: _cache[3] || (_cache[3] = ($event: any) => (showContent.value = true)),
                  customClass: "bg-green-600 hover:bg-green-700 text-white transition border border-green-600 hover:border-green-700 md:w-auto w-full"
                }, {
                  default: _withCtx(() => [
                    _createVNode(_unref(EyeIcon), { class: "w-5 h-5 mr-2 -ml-1 icon" }),
                    _hoisted_7
                  ]),
                  _: 1
                }))
              : _createCommentVNode("", true)
          ]))
        : _createCommentVNode("", true)
    ]),
    rightContent: _withCtx(() => [
      _createElementVNode("div", _hoisted_9, [
        _createVNode(SideNav, {
          hasRecord: newRecord.value,
          item: item.value,
          "other-details-count": otherDetailsCount.value
        }, null, 8, ["hasRecord", "item", "other-details-count"])
      ])
    ]),
    default: _withCtx(() => [
      _createElementVNode("div", _hoisted_8, [
        (loaded.value)
          ? (_openBlock(), _createBlock(FormSection, {
              key: 0,
              newRecord: newRecord.value,
              form: form.value,
              errors: errors.value,
              "additional-data": additionalData.value,
              item: item.value,
              "onUpdate:form": _cache[7] || (_cache[7] = (value) => form.value = value)
            }, {
              default: _withCtx(() => [
                _createVNode(ConfirmationModal, {
                  typeTwo: true,
                  show: showConfirmation.value,
                  onCancel: _cache[4] || (_cache[4] = ($event: any) => (showConfirmation.value = false)),
                  onConfirm: _cache[5] || (_cache[5] = ($event: any) => (save())),
                  "cancel-text": "Cancel",
                  "action-text": "Confirm",
                  title: "Update Record",
                  content: "Are you sure you wish to update this record?"
                }, null, 8, ["show"]),
                _createVNode(SuccessModal, {
                  show: showSuccess.value,
                  onConfirm: _cache[6] || (_cache[6] = ($event: any) => (redirectSuccess())),
                  "action-text": "Close",
                  title: "Record Updated!",
                  content: successMessage.value
                }, null, 8, ["show", "content"])
              ]),
              _: 1
            }, 8, ["newRecord", "form", "errors", "additional-data", "item"]))
          : _createCommentVNode("", true)
      ]),
      _createVNode(ContentModal, {
        show: showContent.value,
        title: "Add Recipient & Attention"
      }, {
        button: _withCtx(() => [
          _createVNode(ButtonComponent, {
            btnWidth: "quinary",
            onClick: _cache[10] || (_cache[10] = ($event: any) => (showContent.value = false))
          }, {
            default: _withCtx(() => [
              _createTextVNode(" Cancel ")
            ]),
            _: 1
          }),
          (_unref(hasPermission)('can-view-patient-case-inpdf'))
            ? (_openBlock(), _createElementBlock("a", {
                key: 0,
                onClick: _cache[11] || (_cache[11] = ($event: any) => (generatePDFAccess('scar')))
              }, [
                _createVNode(ButtonComponent, { customClass: "bg-green-600 hover:bg-green-700 text-white transition border border-green-600 hover:border-green-700" }, {
                  default: _withCtx(() => [
                    _createVNode(_unref(EyeIcon), { class: "w-5 h-5 mr-2 -ml-1 icon" }),
                    _hoisted_10
                  ]),
                  _: 1
                })
              ]))
            : _createCommentVNode("", true)
        ]),
        default: _withCtx(() => [
          _createElementVNode("div", null, [
            _createVNode(TextInput, {
              label: "To:",
              placeholder: "To",
              name: "to",
              id: "to",
              required: "",
              modelValue: formContent.value.to,
              "onUpdate:modelValue": _cache[8] || (_cache[8] = ($event: any) => ((formContent.value.to) = $event)),
              error: errors.value.to
            }, null, 8, ["modelValue", "error"])
          ]),
          _createElementVNode("div", null, [
            _createVNode(TextInput, {
              label: "Attn:",
              placeholder: "Attn",
              name: "attn",
              id: "attn",
              required: "",
              modelValue: formContent.value.attn,
              "onUpdate:modelValue": _cache[9] || (_cache[9] = ($event: any) => ((formContent.value.attn) = $event)),
              error: errors.value.attn
            }, null, 8, ["modelValue", "error"])
          ])
        ]),
        _: 1
      }, 8, ["show"]),
      _createVNode(PageLoader, { show: loading.value }, null, 8, ["show"]),
      _createVNode(ErrorModal, {
        show: showError.value,
        content: showErrorMessage.value
      }, null, 8, ["show", "content"])
    ]),
    _: 1
  }, 8, ["backLink"]))
}
}

})